import { useEffect, useRef, useState } from 'react'

import { createSession, type Membership } from '../../../api/drama'
import { setRenewer, setToken } from '../../../api/request'
import { novaToken, onNovaToken } from '../../../lib/nova'
import { initData, insideTelegram } from '../../../lib/telegram'

type Status = 'pending' | 'ready' | 'anonymous'

interface SessionState {
  status: Status
  membership: Membership | null
}

/**
 * 启动时用 initData 换会话，并把「重建会话」的方式注册给请求层，
 * 供其在 401 时自动重试一次。
 *
 * initData 每次都从 Telegram 现取，不缓存 —— 缓存下来反复用既是重放面，
 * 也会出现「用户改了资料、我们还拿着旧快照」的问题。
 */
export function useSession(source?: string): SessionState {
  const [state, setState] = useState<SessionState>({ status: 'pending', membership: null })
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    // Nova App 的网页容器里：直接用 App 注入的会话令牌；401 时取 App 那边最新的一份（它续期后会更新）
    const fromApp = novaToken()
    if (fromApp) {
      setToken(fromApp)
      setState({ status: 'ready', membership: null })
      onNovaToken((next) => setToken(next))
      setRenewer(async () => {
        const next = novaToken()
        setToken(next)
        return next
      })
      return () => setRenewer(null)
    }

    // Telegram 客户端外打开：拿不到 initData，以匿名身份继续，只能看免费集。
    if (!insideTelegram()) {
      setToken(null)
      setState({ status: 'anonymous', membership: null })
      return
    }

    const establish = async (): Promise<string | null> => {
      const s = await createSession(initData(), source)
      setToken(s.token)
      // membership 现在是可选的：App 阶段多了 Apple / Google / 邮箱登录，
      // 那几种身份没有频道成员和机器人关注状态。Mini App 这边一定有，
      // 但类型上要接住那个可能的 undefined。
      setState({ status: 'ready', membership: s.membership ?? null })
      return s.token
    }

    setRenewer(establish)
    establish().catch(() => {
      // 换会话失败就退回匿名，页面仍能显示免费集，不至于白屏
      setToken(null)
      setState({ status: 'anonymous', membership: null })
    })

    return () => setRenewer(null)
  }, [source])

  return state
}
